import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Building2, Calendar, CheckCircle2, LogIn, LogOut as LogOutIcon, X as XIcon, IndianRupee, User as UserIcon } from "lucide-react";

const STATUS_FLOW = {
  pending:     { color: "bg-sun/40 text-ink",     next: [{ s: "confirmed",   label: "Confirm",       icon: CheckCircle2 }, { s: "cancelled", label: "Cancel", icon: XIcon }] },
  upcoming:    { color: "bg-sun/40 text-ink",     next: [{ s: "confirmed",   label: "Confirm",       icon: CheckCircle2 }, { s: "cancelled", label: "Cancel", icon: XIcon }] },
  confirmed:   { color: "bg-sky/15 text-sky",     next: [{ s: "checked_in",  label: "Check in",      icon: LogIn },        { s: "cancelled", label: "Cancel", icon: XIcon }] },
  checked_in:  { color: "bg-brand/15 text-brand", next: [{ s: "checked_out", label: "Check out & earn", icon: LogOutIcon }] },
  checked_out: { color: "bg-leaf/40 text-ink",    next: [] },
  completed:   { color: "bg-leaf/40 text-ink",    next: [] },
  cancelled:   { color: "bg-surface-muted text-ink-soft", next: [] },
};

export default function HostelBookings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/partner/hostel/bookings`).then(r => setData(r.data)).catch(() => setData("err"));
  useEffect(() => { load(); }, []);

  const transition = async (b, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this booking?")) return;
    if (status === "checked_out" && !window.confirm("Confirm check-out? This will log earnings to your weekly payout.")) return;
    setBusy(b.booking_id);
    try {
      await axios.patch(`${API}/partner/hostel/bookings/${b.booking_id}`, { status });
      load();
    } finally { setBusy(""); }
  };

  if (!user) return null;
  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <div className="text-ink-soft">Set up your hostel first via <a href="/app/partner" className="text-brand font-bold">Settings</a>.</div>;

  const items = data.items || [];
  const counts = items.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="hostel-bookings-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><Building2 className="w-4 h-4"/>Hostel · bookings</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Stay pipeline</h1>
        <p className="text-ink-soft text-sm">Move bookings through the flow: pending → confirmed → checked in → checked out. Earnings are logged on check-out.</p>
      </header>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
        {Object.entries(STATUS_FLOW).map(([s]) => (
          <div key={s} className="bg-surface border border-line rounded-2xl p-3 text-center">
            <div className="font-heading text-xl font-extrabold">{counts[s] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft mt-0.5">{s.replace(/_/g, " ")}</div>
          </div>
        ))}
      </div>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        {items.length === 0 ? (
          <p className="text-ink-soft text-sm">No bookings yet. Share your hostel link with pet parents to start receiving requests.</p>
        ) : (
          <div className="divide-y divide-line">
            {items.map(b => (
              <div key={b.booking_id} data-testid={`booking-${b.booking_id}`} className="py-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="flex-1 min-w-[180px]">
                  <div className="font-heading font-extrabold">{b.pet_name} <span className="text-ink-soft font-normal text-sm">· {b.days} days</span></div>
                  <div className="text-xs text-ink-soft inline-flex items-center gap-1.5 mt-0.5">
                    <UserIcon className="w-3 h-3"/> {b.user_name || b.contact_name || "—"} <span className="opacity-50">·</span> <Calendar className="w-3 h-3"/> {b.start_date || new Date(b.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-heading font-extrabold"><IndianRupee className="w-3.5 h-3.5"/>{b.amount_inr || b.total || 0}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${STATUS_FLOW[b.status]?.color || "bg-surface-muted text-ink-soft"}`}>{(b.status || "pending").replace(/_/g, " ")}</span>
                <div className="flex gap-1.5 flex-wrap">
                  {(STATUS_FLOW[b.status]?.next || []).map(({ s, label, icon: I }) => (
                    <button key={s} onClick={() => transition(b, s)} disabled={busy === b.booking_id} data-testid={`btn-${s}-${b.booking_id}`} className={`rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50 ${s === "cancelled" ? "bg-surface-muted text-ink-soft hover:bg-brand/10" : "bg-brand text-white hover:bg-brand-hover"}`}>
                      <I className="w-3 h-3"/>{label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
